import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  UserPlus,
  Search,
  Edit,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import UserPlusIcon from "@/components/icons/UserPlusIcon";
import EditIcon from "@/components/icons/EditIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";

const ClientIndexPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, client: null });
  const [isSearching, setIsSearching] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const { data, isLoading, error } = useQuery(
    ["clients", page, debouncedSearch, statusFilter],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { is_active: statusFilter }),
      });
      const response = await api.get(`/clients?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Always refetch on mount so navigating back from Edit shows latest
      refetchOnMount: "always",
    }
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const deleteMutation = useMutation((clientId) => api.delete(`/clients/${clientId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      toast.success("Client deleted successfully");
      setDeleteDialog({ isOpen: false, client: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete client");
    },
  });

  const handleDeleteClick = (client) => {
    setDeleteDialog({
      isOpen: true,
      client: client,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.client) {
      deleteMutation.mutate(deleteDialog.client.id);
    }
  };

  const getStatusBadgeVariant = (status) => {
    return status ? "default" : "secondary";
  };

  return (
    <div className="">
      <div className="">
        {/* Header */}
        <div className="flex flex-col gap-3 slide-up lg:flex-row lg:items-center lg:justify-between">
          <div className="slide-up">
            <Link to="/clients/create">
              <Button className="w-full shadow-lg hover:bg-red-900 hover:shadow-xl sm:w-auto">
                <UserPlusIcon className="w-5 h-5 mr-2" />
                Add Client
              </Button>
            </Link>
          </div>
          {/* Filters */}
          <div className="flex flex-col gap-3 lg:items-end">
            <div className="grid w-full gap-3 sm:grid-cols-2 lg:auto-cols-fr lg:grid-flow-col lg:items-end">
              <div className="min-w-0">
                <div className="relative">
                  <Input
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pr-10 border-primary/20 focus:border-primary/40"
                    aria-label="Search clients"
                  />
                  <Search
                    className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Filter by status">
                      {statusFilter === ""
                        ? "All Status"
                        : statusFilter === "true"
                          ? "Active"
                          : statusFilter === "false"
                            ? "Inactive"
                            : "Filter by status"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-50 text-gray-400 rounded-b-lg shadow-lg bg-navy-100">
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleClearFilters} className="w-full hover:bg-red-900 sm:w-auto">
                  <CloseIcon className="w-5 h-5 mr-2" />
                  <span className="whitespace-nowrap">Clear</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64 slide-up">
            <div className="text-center">
              <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
              <p className="text-gray-600">Loading clients...</p>
            </div>
          </div>
        ) : (
          <div className="shadow-sm slide-up backdrop-blur-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 whitespace-nowrap hover:bg-gray-50/50">
                  <TableHead className="sticky md:sticky md:left-0 md:top-0">Company</TableHead>
                  <TableHead className="w-1/3">Company Contact First Name</TableHead>
                  <TableHead className="w-1/3">Company Contact Last Name</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isSearching ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center">
                      <div className="text-center">
                        <LoadingSpinner size="lg" />
                        <p className="mt-4 text-gray-600">Searching clients...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : !data?.data || data.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No clients found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.data.map((client) => (
                    <TableRow key={client.id} >
                      <TableCell className="sticky max-w-80 md:sticky md:left-0 md:top-0">
                        <div className="p-4 my-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0 ">
                          {client.name || "N/A"}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-80">
                        <div className="p-4 mx-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                          {client.contact_first_name || "N/A"}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-80">
                        <div className="p-4 mx-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                          {client.contact_last_name || "N/A"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="p-4 m-2 text-center uppercase rounded-t-lg bg-navy-100 text-gray-0">
                          <Badge
                            variant={getStatusBadgeVariant(client.is_active)}
                            className={`border-none p-0 ${client.is_active ? "text-green-500" : "text-red-600"
                              }`}
                          >
                            {client.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="w-4 text-right">
                        <div className="p-2 my-2 ml-2 text-center rounded-t-lg bg-navy-100 text-gray-0 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            <Link to={`/clients/${client.id}/edit`}>
                              <Button variant="outline p-0" size="sm" className="rounded-lg">
                                <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                              </Button>
                            </Link>
                            <Button
                              variant="outline p-0"
                              size="sm"
                              className="hover:text-red-600"
                              onClick={() => handleDeleteClick(client)}
                              disabled={client.usage_count > 0}
                            >
                              <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col pr-0 border-t border-b slide-up border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
          <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
            <span className="block sm:inline">
              Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, data?.pagination?.total || 0)}{" "}
              of {data?.pagination?.total || 0} results{" "}
            </span>
          </div>
          <div className="flex items-center justify-center w-full sm:w-auto">
            <Button
              variant="outline"
              className="flex items-center justify-center flex-1 h-auto p-3 text-xs font-normal uppercase border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100 sm:flex-none sm:p-5"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
              PREVIOUS
            </Button>
            <Button
              variant="outline"
              className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
            >
              {page}
            </Button>
            <Button
              variant="outline"
              className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= data?.pagination?.pages}
            >
              NEXT
              <ChevronRight className="w-4 h-4 ml-2 text-red-500" />
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, client: null })}
          onConfirm={handleDeleteConfirm}
          userName={
            deleteDialog.client
              ? `${deleteDialog.client.name}`
              : ""
          }
          title="Delete Client"
          isLoading={deleteMutation.isLoading}
        />
      </div>
    </div>
  );
};
export default ClientIndexPage;
